import { gql } from '@apollo/client';

const ScheduledChangesForScheduledChangesTabPPC = gql`
  fragment ScheduledChangesForScheduledChangesTabPPC on ScheduledChanges {
    nextChangeTimestamp
    subscriptionScheduleAction
    transactionAccountId
    invoiceGroupId
    offering {
      ...OfferingFieldsToCompare
    }
    pricingPlan {
      ...PricingPlanFieldsToCompare
    }
    promotions {
      benefits {
        value
        values {
          appliedOn
          value
        }
        benefitType
        subBenefitType
        duration
        iterations
        customisable {
          value {
            type
            lowerBound
            upperBound
          }
          iterations {
            type
            lowerBound
            upperBound
          }
          duration
        }
      }
      eligibilityRules {
        currency
      }
      promotionInstanceId
      promotionWindow {
        startTime
        endTime
      }
      id
      status
      rule
      title
      type
      pricingType
      cycleInterval
      applicationReason {
        id
        readableName
        customisable {
          id {
            type
            anyOf {
              readableName
              id
            }
          }
        }
      }
      isPromotionCustomisable
    }
    chargeQuantities {
      chargeElement
      quantity
    }
    scheduledBillingInvoiceGroupId
  }
`;

const OfferingFieldsToCompare = gql`
  fragment OfferingFieldsToCompare on Offering {
    id
    name
  }
`;

const PricingPlanFieldsToCompare = gql`
  fragment PricingPlanFieldsToCompare on PricingPlan {
    type
    billingFrequency
  }
`;

export const fetchEntitlementDetailsPPCPerfV2 = gql`
  ${ScheduledChangesForScheduledChangesTabPPC}
  ${OfferingFieldsToCompare}
  ${PricingPlanFieldsToCompare}
  query fetchEntitlementDetailsPPCPerfV2(
    $id: String
    $transactionAccountId: String
    $fetchIsExternallyBilled: Boolean = false
  ) {
    entitlementDetails(id: $id, filters: { transactionAccountId: $transactionAccountId }) {
      logoId
      createdDate
      termDetails {
        paymentMethod
        totalCreditLimit
      }
      isFutureDatedSubscription
      futureDatedStartTimestamp
      billingFrequency: billingCycle
      billingStatus
      cloudSiteHostname
      cloudSiteCustomHostname
      cloudSiteId
      cloudSiteSen
      endTimestamp
      entitlementNumber
      updatePaymentMethodLink
      hasPendingInvoices
      invoices(shouldFilterByEntitlementTxA: true) {
        nodes {
          id
          createdDate
          grandTotal
          paymentCurrency
          pricingCurrency
          lineItems {
            entitlementId
            id
            discounts {
              amount
              type
              percent
              reasonCode
              percent
            }
            margins {
              amount
              type
              percent
              promoCode
            }
          }
        }
      }
      id
      latestPeriodEndDate
      latestPeriodStartDate
      transactionAccountId
      billingGroupId
      licenseLevel
      offeringId
      isOptOutAllowed
      edition
      parentSen
      parentCloudSiteName
      platform
      productId
      nextBillingTimestamp
      productName
      productType
      baseProduct
      renewalAction
      sourceSystem
      plan
      subscriptionId
      unitCount
      offering {
        id
        name
        productKey
        level
        type
        availableSyntheticPricingPlans {
          type
          status
          discountPercentage
        }
        pricingType
        status
        expiryDate
      }
      promotions {
        id
        title
        type
        benefits {
          benefitType
          duration
          subBenefitType
          iterations
          value
          values {
            appliedOn
            value
          }
          scale
          type
          customisable {
            value {
              type
              lowerBound
              upperBound
            }
            iterations {
              type
              lowerBound
              upperBound
            }
            duration
          }
        }
        promotionInstanceId
        promotionWindow {
          endTime
          startTime
        }
        status
        cycleInterval
        pricingType
        eligibilityRules {
          products
          cycleInterval
          pricingType
          partnerType
          partnerTier
          saleTransitionType
          currency
        }
        cycleIntervalWithEligibleConditions
        applicationReason {
          id
          readableName
          customisable {
            id {
              type
              anyOf {
                readableName
                id
              }
            }
          }
        }
        isPromotionCustomisable
      }
      trialPromotionDetails {
        totalTrialDays
        totalTrialCount
      }
      subscriptionTimestamps {
        firstStartTimestamp
        lastEndTimestamp
        firstTrialStartTimestamp
        lastTrialEndTimestamp
      }
      offering {
        id
        name
      }
      pricingPlan {
        type
        billingFrequency
        id
        currency
        pricingPlanType
        status
        description
        tiers {
          unitAmount
          flatAmount
          floor
          ceiling
        }
      }
      scheduledChanges {
        ...ScheduledChangesForScheduledChangesTabPPC
      }
      nextBillEstimate {
        total
        currency
        renewalCharge
        proratedCharge
        lastBillEstimateItem {
          adjustments {
            type
            amount
            percent
          }
          margins {
            type
            amount
            percent
          }
        }
        nextCyclePartnerDiscountOrMargin {
          amount
          percent
          promotionId
        }
      }
      activeSubscriptionBillingGroupId
      subscription {
        subscriptionPeriod {
          offeringKey
          startTimestamp
          trial {
            startTimestamp
            endTimestamp
          }
          chargeQuantities {
            quantity
          }
        }
      }
      activeUsers
      provisioningStatus
      isSandbox
      isApp
      isAddOn
      userTier
      chargeElement
      deletedProductDetails {
        status
        datePlaced
        deletedOn
        author
        supportTicket
        internalComment
      }
      relatesToEntitlements {
        entitlementId
        relationshipType
      }
      relatesFromEntitlements {
        entitlementId
        relationshipType
      }
      showExtendTrial
      productStatus
      upgradeTrial {
        pricingPlan {
          type
          billingFrequency
          id
          currency
          pricingPlanType
          status
          description
          tiers {
            unitAmount
            flatAmount
            floor
            ceiling
          }
        }
        offeringId
        offeringName
        startTimestamp
        endTimestamp
      }
      pauseBilling {
        startTimestamp
        endTimestamp
      }
      provisionedResourceDetails {
        organisationId
        organisationName
        provisionedResourceType
      }
      isExternallyBilled @include(if: $fetchIsExternallyBilled)
      metadata {
        contractId @include(if: $fetchIsExternallyBilled)
        contractType @include(if: $fetchIsExternallyBilled)
      }
    }
  }
`;
